import React, { ReactNode } from 'react';
import { Calculator, Box, Warehouse, Plane, Compass, CircleUserRound, ShieldCheck, Heart, LayoutTemplate, MapPin } from 'lucide-react';
import { AppLanguage, DEFAULT_LANGUAGE } from '../i18n/language';

export interface NavItem {
    label: string;
    path?: string; // If missing, might be a header or action
    disabled?: boolean;
    icon?: ReactNode;
    adminOnly?: boolean;
    isExternal?: boolean;
    // Function to determine if this item is active based on current path
    activeMatch?: (currentPath: string) => boolean;
}

export interface NavCategory {
    title: string;
    items: NavItem[];
}

const NAV_TEXT = {
    zh: {
        catCalculator: "跨境物流计算器",
        catService: "跨境物流服务",
        catMine: "我的",
        catAbout: "关于海海",
        catAdmin: "管理员入口",
        calculator: "跨境物流计算器",
        dropshipping: "海外仓一件代发",
        shipping: "海外快递",
        myProducts: "我的商品",
        myAddress: "我的地址",
        myWallet: "我的钱包",
        about: "关于海海",
        xiaohongshu: "海海小红书",
        adminBackend: "海海后台",
        adminFront: "海海前端",
    },
    en: {
        catCalculator: "Calculator",
        catService: "Shipping Services",
        catMine: "My",
        catAbout: "About",
        catAdmin: "Admin",
        calculator: "Cross-border calculator",
        dropshipping: "Overseas warehouse dropshipping",
        shipping: "Overseas express",
        myProducts: "My products",
        myAddress: "My addresses",
        myWallet: "My wallet",
        about: "About SSEEAA",
        xiaohongshu: "Xiaohongshu",
        adminBackend: "Admin backend",
        adminFront: "Admin frontend",
    },
} as const;

export const getNavigationCategories = (language: AppLanguage): NavCategory[] => {
    const t = NAV_TEXT[language] || NAV_TEXT[DEFAULT_LANGUAGE];
    return [
        {
            title: t.catCalculator,
            items: [
                {
                    label: t.calculator,
                    path: "/",
                    icon: <Calculator />,
                    activeMatch: (p) => p === '/'
                }
            ]
        },
        {
            title: t.catService,
            items: [
                {
                    label: t.dropshipping,
                    path: "/dropshipping",
                    icon: <Warehouse />,
                    activeMatch: (p) => p.startsWith('/dropshipping')
                },
                {
                    label: t.shipping,
                    path: "/shipping",
                    icon: <Plane />,
                    activeMatch: (p) => p === '/shipping' ||
                        p === '/labeling-overview' ||
                        p.startsWith('/label-management') ||
                        (p.startsWith('/labeling') &&
                            !p.startsWith('/labeling-admin') &&
                            !p.startsWith('/labeling-billing') &&
                            !p.startsWith('/labeling-sales-dashboard'))
                }
            ]
        },
        {
            title: t.catMine,
            items: [
                {
                    label: t.myProducts,
                    path: "/products",
                    icon: <Box />,
                    activeMatch: (p) => p.startsWith('/products')
                },
                {
                    label: t.myAddress,
                    path: "/address-book",
                    icon: <MapPin />,
                    activeMatch: (p) => p.startsWith('/address-book')
                },
                {
                    label: t.myWallet,
                    path: "/account",
                    icon: <CircleUserRound />,
                    activeMatch: (p) => p.startsWith('/account')
                }
            ]
        },
        {
            title: t.catAbout,
            items: [
                {
                    label: t.about,
                    path: "/about",
                    icon: <Compass />,
                    activeMatch: (p) => p.startsWith('/about')
                },
                {
                    label: t.xiaohongshu,
                    path: "https://www.xiaohongshu.com/user/profile/6712b033000000001d020291",
                    icon: <Heart />,
                    isExternal: true
                }
            ]
        },
        {
            title: t.catAdmin,
            items: [
                {
                    label: t.adminBackend,
                    path: "/admin",
                    icon: <ShieldCheck />,
                    adminOnly: true,
                    activeMatch: (p) => p.startsWith('/admin')
                },
                {
                    label: t.adminFront,
                    path: "/front",
                    icon: <LayoutTemplate />,
                    adminOnly: true,
                    activeMatch: (p) => p === '/front'
                }
            ]
        }
    ];
};

// Backward compatibility for callers that still use a constant.
export const navigationCategories: NavCategory[] = getNavigationCategories(DEFAULT_LANGUAGE);
